import type { DocumentDetail } from '@avisoma/shared';
import AuthGuard from '../../components/AuthGuard';
import { DocumentProcessingStatus } from '../../components/DocumentProcessingStatus';
import RecoverStrandedButton from '../../components/features/RecoverStrandedButton';
import backend from '../../services/backend';
import { useQueryClient } from '@tanstack/react-query';
import { pagesRowCounts } from './pagesRowCounts';
import { reconstructionRowCounts } from './reconstructionRowCounts';
import { hidesFullSuccessOnStageError } from './stageErrorBar';

type StepProgress = {
  status?: 'done' | 'error' | 'skip' | 'processing' | 'pages-done';
  // DR-2426: projected array counts (the id arrays no longer cross the wire).
  completedCount?: number;
  erroredCount?: number;
  skippedCount?: number;
  // DR-2395: server-derived live per-page counts (present on the per-page stages).
  counts?: { done: number; processing: number; error: number; skip: number; pending: number };
  errorMessage?: string;
  time?: number;
  startedAt?: string;
  completedAt?: string;
};

type RetryRunner = Parameters<typeof backend.retryDocument>[1];

interface ProcessingStatusCardProps {
  document: DocumentDetail;
  queryId: string | undefined;
  strandedCount: number;
}

export function ProcessingStatusCard({ document, queryId, strandedCount }: ProcessingStatusCardProps) {
  const queryClient = useQueryClient();
  // DR-2426: deduped page count comes straight off the wire now (the id array is projected
  // out server-side); it's the "N unique pages" total every stage row measures against.
  const uniquePageIdsLength = document.processing?.pages?.uniquePageCount ?? 0;
  const pagesCounts = pagesRowCounts(document.processing?.pages?.status, uniquePageIdsLength);
  const isImage = document.mimeType?.startsWith('image/');
  // DR-3149: the doc-level reconstruction row is the one set of counts this card projects
  // itself; the helper keeps them consistent with the per-page stages' wire contract.
  const reconstructionCounts = reconstructionRowCounts(document.processing?.reconstruction?.status);

  const retry = async (step: RetryRunner) => {
    const response = await backend.retryDocument(document.id, step);
    if ('dryRun' in response.data) return;
    queryClient.setQueryData(['document', queryId], response.data);
  };

  const steps: Array<{
    title: string;
    progress: StepProgress | undefined;
    totalPages: number;
    step: RetryRunner;
  }> = [
    {
      title: isImage ? 'Image Processor' : 'Pages',
      progress: {
        status: document.processing?.pages?.status as StepProgress['status'],
        time: document.processing?.pages?.time,
        completedCount: pagesCounts.completedCount,
        erroredCount: pagesCounts.erroredCount,
        skippedCount: pagesCounts.skippedCount,
        errorMessage: document.processing?.pages?.errorMessage,
      },
      totalPages: uniquePageIdsLength,
      step: 'pdf-splitter',
    },
    {
      title: 'Parser',
      progress: document.processing?.pdfParser as StepProgress | undefined,
      totalPages: uniquePageIdsLength,
      step: 'pdf-parser',
    },
    {
      title: 'Metadata',
      progress: document.processing?.metadata as StepProgress | undefined,
      totalPages: uniquePageIdsLength,
      step: 'metadata',
    },
    {
      title: 'Embeddings',
      progress: document.processing?.embeddings as StepProgress | undefined,
      totalPages: uniquePageIdsLength,
      step: 'embeddings',
    },
    {
      title: 'Classification',
      progress: document.processing?.classification as StepProgress | undefined,
      totalPages: uniquePageIdsLength,
      step: 'classification',
    },
    {
      title: 'Reconstruction',
      progress: {
        ...(document.processing?.reconstruction as StepProgress | undefined),
        completedCount: reconstructionCounts.completedCount,
        erroredCount: reconstructionCounts.erroredCount,
        skippedCount: reconstructionCounts.skippedCount,
      },
      totalPages: reconstructionCounts.totalPages,
      step: 'reconstruction',
    },
    {
      title: 'Data Extraction',
      progress: document.processing?.dataExtraction as StepProgress | undefined,
      totalPages: uniquePageIdsLength,
      step: 'data-extraction',
    },
  ];

  return (
    <div className="bg-white dark:bg-zinc-800 shadow rounded-lg overflow-hidden">
      <div className="px-3 py-3 sm:px-4 sm:py-4 border-b border-gray-200 dark:border-zinc-700 flex items-center justify-between">
        <h2 className="text-base font-medium text-gray-900 dark:text-zinc-100">Processing Status</h2>
        <div className="flex items-center gap-2">
          <AuthGuard roles={['admin', 'curator']}>
            <RecoverStrandedButton documentId={document.id} count={strandedCount} />
          </AuthGuard>
        </div>
      </div>
      <div className="px-3 py-3 sm:px-4 sm:py-4 space-y-1">
        <div className="space-y-2">
          {steps.map(({ title, progress, totalPages, step }) => (
            <DocumentProcessingStatus
              key={step}
              title={title}
              progress={progress}
              totalPages={totalPages}
              hideFullSuccessOnStageError={hidesFullSuccessOnStageError(step)}
              onRetry={() => retry(step)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
